/* global atob */

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
// @ts-ignore Ignore unused commented imports
import { CoreActions } from '@next/core-lib';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';

// @ts-ignore Ignore unused commented ActionTypes import
import { Action, Store } from '@ngrx/store';

import { combineLatest, Observable, of } from 'rxjs';

import {
  catchError,
  concatMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { AccessRule } from '../../../shared/models/authorizationService.model';
import { AuthorizationService } from '../../../shared/services/authorization.service';

import { ProfileModuleState } from '../../models/profile.model';
import { UserProfile } from '../../models/userProfile.model';

import { AutoSuggestService } from '../../services/autosuggest.service';
import * as profileActions from '../actions/profile.actions';
import {
  initAuthorization,
  setAccessRules,
  setApplicationGroups,
  setApplications,
  setRoles,
} from '../actions/profile.actions';
import { selectProfileId } from '../selectors/profile.selectors';

@Injectable()
export class ProfileEffects {
  searchProfiles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.searchProfilesRequest),
      mergeMap(({ searchtext }) =>
        this.authorizationService.getSearchProfiles(searchtext.toString()).pipe(
          map((profile: UserProfile) => {
            const applicationLicensesDeCompressed = AuthorizationService.getDataFromClaims(
              profile.applicationsJson.toString(),
              atob,
            );
            const profilePropertiesDeCompressed = AuthorizationService.getDataFromClaims(
              profile.propertiesJson.toString(),
              atob,
            );
            const userprofile = {
              ...profile,
              applicationsJson: applicationLicensesDeCompressed,
              propertiesJson: profilePropertiesDeCompressed,
            };
            return profileActions.searchProfilesSuccess({ profile: userprofile });
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of(
                profileActions.searchProfileNotFound(),
                profileActions.clearProfileResult(),
              );
            } else {
              return of(
                NotificationActions.errorSnackbar({ message: error.message }),
                profileActions.clearProfileResult(),
              );
            }
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  autoSuggest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.autoSuggestProfiles),
      mergeMap(({ searchtext }) =>
        this.autoSuggestService.autoSuggestProfiles(searchtext).pipe(
          map((autoSuggestResults) =>
            profileActions.autoSuggestProfilesSuccess({
              autoSuggest: autoSuggestResults,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
        ),
      ),
    ),
  );

  setUseraccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.setUseraccess),
      withLatestFrom(this.store.select(selectProfileId)),
      switchMap(([action, profileId]: [{ applicationKey: string; roleKey: string }, number]) =>
        this.authorizationService
          .setUseraccess(profileId, action.applicationKey, action.roleKey)
          .pipe(
            concatMap(() => [CoreActions.setLoading({ loading: false })]),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
      catchError((error: HttpErrorResponse) =>
        of(NotificationActions.errorSnackbar({ message: error.message })),
      ),
    ),
  );

  initAuthorization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initAuthorization),
      switchMap(({ profileId }) =>
        combineLatest([
          this.authorizationService.getApplications(),
          this.authorizationService.getApplicationGroups(),
          this.authorizationService.getRoles(),
          this.authorizationService
            .getUserAccess(profileId)
            .pipe(map((accessRules: AccessRule[]) => accessRules as AccessRule[])),
        ]).pipe(
          concatMap(([applications, applicationGroups, roles, accessRules]) => [
            setApplications({ applications }),
            setApplicationGroups({ applicationGroups }),
            setAccessRules({ accessRules }),
            setRoles({ roles }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly authorizationService: AuthorizationService,
    private readonly autoSuggestService: AutoSuggestService,
    private store: Store<ProfileModuleState>,
  ) {}
}
